import Container from '~/components/Container';
import ProductList from '~/components/ProductList';
import { useTranslation } from 'react-i18next';
import useProductsKeepState from '~/hooks/useProductsKeepState';
import { useEffect, useState } from 'react';
import { PRODUCT_ORDER_BY } from '../../constants';
import { extractProductEventData } from '~/utils/product';
import { Product } from '~/services/kup/models/Product';
import useBatchLogger from '~/hooks/useBatchLogger';
import Typography from '~/components/Typography.tsx';
import { SelectChangeEvent } from '@mui/material';
import { styled } from '@mui/material/styles';
import Select from '~/components/Select.tsx';
import { useLocation, useNavigate } from 'react-router-dom';

type ProductsInBrandProps = {
  brandSlug: string;
};

const StyledSelect = styled(Select)((props) => ({
  padding: '0px',
  color: props.theme.palette.typography.primary,
  ...props.theme.typography.BaseS,
  marginRight: '-8px',
  '& .MuiInputBase-root': {
    padding: '0px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
}));

export default function ProductsInBrand({ brandSlug }: ProductsInBrandProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const query = location.search;
  const orderBy = new URLSearchParams(query).get('orderBy') ?? undefined;
  const pathname = location.pathname;

  const { i18n } = useTranslation();
  const { logAction } = useBatchLogger();
  const [selectedOrderBy, setSelectedOrderBy] = useState<string>(orderBy ?? 'recommendation');
  const { products, total, hasNextPage, next } = useProductsKeepState(
    `brand:${brandSlug}:${selectedOrderBy}`,
    {
      brand: brandSlug,
      excludeSoldOut: false,
      orderBy: selectedOrderBy?.split(':')[0] ?? undefined,
      orderMethod: selectedOrderBy?.split(':')[1] ?? undefined,
    },
    { pageSize: 8 }
  );

  useEffect(() => {
    if (selectedOrderBy !== orderBy) {
      const navigatePath = pathname + (selectedOrderBy ? `?orderBy=${selectedOrderBy}` : '');
      navigate(navigatePath, { replace: true, state: { restoreScroll: false } });
    }
  }, [pathname, navigate, selectedOrderBy, orderBy]);

  const menus = PRODUCT_ORDER_BY.map((orderBy) => ({
    label: i18n.t(orderBy.label),
    value: orderBy.value,
  }));
  const orderValue = PRODUCT_ORDER_BY.find((orderBy) => orderBy.value === selectedOrderBy)?.value;

  const handleClickProductItem = (product: Product) => {
    logAction({
      eventType: 'CLICK',
      eventTarget: 'BRAND_PRODUCT',
      eventCategory: 'product',
      eventData: extractProductEventData(product, { type: 'brand' }),
    });
  };

  const handleLikeProduct = (product: Product) => {
    if (!product.isLiked) {
      logAction({
        eventType: 'CLICK',
        eventTarget: 'BRAND_PRODUCT_LIKE',
        eventCategory: 'product',
        eventData: extractProductEventData(product, { type: 'brand' }),
      });
    }
  };

  return (
    <Container>
      <ProductList
        type={'pair'}
        title={
          <Typography variant={'BaseS'}>
            {i18n.t('component-product-count', { count: total })}
          </Typography>
        }
        trail={
          <StyledSelect
            menus={menus}
            value={orderValue}
            onChange={(e: SelectChangeEvent<unknown>) => {
              setSelectedOrderBy(e.target.value as string);
            }}
          />
        }
        products={products}
        onInfiniteScroll={
          hasNextPage
            ? () => {
                next();
              }
            : undefined
        }
        onClickProductItem={handleClickProductItem}
        onLikeProductItem={handleLikeProduct}
      />
    </Container>
  );
}
